import React, { useMemo } from 'react'
import styled from 'styled-components'
import cx from 'classnames'
import { useTranslation } from 'react-i18next'
import { DataRoomDocument } from 'silta-ai-backend'
import { SidebarSection } from './Sidebar'
import { themeVariables } from '../themes/themeVariables'
import { Icon } from './Icon'
import { Button } from './Button'
import { downloadDocument } from '../utils/download'
import { Badge } from './Badge'
import { SkeletonParagraph } from './SkeletonParagraph'

const DocumentLink = styled.div`
    display: block;
    margin-bottom: 30px;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: break-word;
    font-weight: 600;
    color: ${themeVariables.colors.brand};
    &:hover {
        color: ${themeVariables.colors.brand};
    }
    cursor: pointer;
`

const SidebarIcon = styled(Icon)`
    width: 14px;
    height: 14px;
    svg {
        width: 14px;
        height: 14px;
    }
`

type DocumentSidebarContentProps = {
    document: DataRoomDocument
    triggerDeleteDocument: (document: DataRoomDocument) => void
    canDeleteDocument: boolean
}

const MAX_DESCRIPTION_LENGTH = 1500

export const DocumentSidebarContent = ({
    document,
    triggerDeleteDocument,
    canDeleteDocument,
}: DocumentSidebarContentProps) => {
    const { t } = useTranslation()
    const truncatedDescription = useMemo(() => {
        if (!document.description) {
            return ''
        }
        return document.description.length > MAX_DESCRIPTION_LENGTH
            ? `${document.description.slice(0, MAX_DESCRIPTION_LENGTH)}...`
            : document.description
    }, [document])
    return (
        <>
            <SidebarSection title="File information" emphasized>
                <DocumentLink
                    onClick={() => {
                        downloadDocument(document)
                    }}
                >
                    {document.originalFileName}
                </DocumentLink>
                <div className={cx('d-flex', 'g-10')}>
                    <Button
                        $variant="secondary"
                        className={cx('d-flex', 'g-5', 'align-items-center')}
                        onClick={() => {
                            downloadDocument(document)
                        }}
                    >
                        <SidebarIcon name="download" />
                        Download
                    </Button>
                    <Button
                        $variant="secondary"
                        className={cx('d-flex', 'g-5', 'align-items-center')}
                        onClick={() => {
                            triggerDeleteDocument(document)
                        }}
                        disabled={!canDeleteDocument}
                    >
                        <SidebarIcon name="trash" />
                        Delete
                    </Button>
                </div>
            </SidebarSection>
            {document.status !== 'error' && (
                <SidebarSection title="Document title & description">
                    {document.status === 'ready' ? (
                        <p className="emphasized">{document.title}</p>
                    ) : (
                        <SkeletonParagraph
                            fullLine
                            length={100}
                            fontSize={14}
                        />
                    )}
                    {document.status === 'ready' ? (
                        <p className="secondary">{truncatedDescription}</p>
                    ) : (
                        <SkeletonParagraph
                            className="m-t-20"
                            length={600}
                            fontSize={14}
                        />
                    )}
                </SidebarSection>
            )}
            {document.status === 'error' && (
                <SidebarSection title="">
                    <Badge $variant="negative">
                        An error occurred while processing the document and it
                        will not be used to generate a{' '}
                        {t('assessment.singular')}. You can try to remove and
                        upload it again.
                    </Badge>
                </SidebarSection>
            )}
        </>
    )
}
